import Link from "next/link";
import { GraduationCap } from "lucide-react";

type Size = "sm" | "md" | "lg" | "xl";
type Variant = "dark" | "light" | "auto";

interface NextSchoolLogoProps {
  /** Wrapping link target. Pass `null` to render without a link. */
  href?: string | null;
  /** Tailwind-driven sizing scale. */
  size?: Size;
  /** `dark` = ciemny tekst (na jasnym tle). `light` = biały tekst (na ciemnym tle). */
  variant?: Variant;
  /** Hide the wordmark and render the icon only. */
  iconOnly?: boolean;
  /** Extra wrapper classes. */
  className?: string;
}

const SIZE_CLASSES: Record<Size, { box: string; icon: string; text: string; gap: string; radius: string }> = {
  sm: { box: "w-8 h-8",  icon: "w-4 h-4", text: "text-base", gap: "gap-2",   radius: "rounded-xl"  },
  md: { box: "w-10 h-10", icon: "w-5 h-5", text: "text-xl",  gap: "gap-2.5", radius: "rounded-2xl" },
  lg: { box: "w-12 h-12", icon: "w-6 h-6", text: "text-2xl", gap: "gap-3",   radius: "rounded-2xl" },
  xl: { box: "w-16 h-16", icon: "w-8 h-8", text: "text-3xl", gap: "gap-3.5", radius: "rounded-3xl" },
};

/**
 * Oficjalny znak marki NextSchool — gradientowy zaokrąglony kwadrat
 * z białą ikoną GraduationCap (lekko obróconą) oraz wordmark "NextSchool".
 *
 * Używaj wszędzie tam, gdzie wcześniej był `Next<span>School</span>` placeholder.
 */
export default function NextSchoolLogo({
  href = "/",
  size = "md",
  variant = "dark",
  iconOnly = false,
  className = "",
}: NextSchoolLogoProps) {
  const s = SIZE_CLASSES[size];
  const textColor =
    variant === "light"
      ? "text-white"
      : variant === "auto"
        ? "text-slate-900 dark:text-white"
        : "text-slate-900";

  const inner = (
    <span className={`inline-flex items-center ${s.gap} ${className}`}>
      <span
        className={`${s.box} ${s.radius} bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center shadow-lg shadow-blue-500/30 rotate-[-6deg]`}
        aria-hidden="true"
      >
        <GraduationCap className={`${s.icon} text-white`} />
      </span>
      {!iconOnly && (
        <span className={`${s.text} font-extrabold tracking-tight ${textColor}`}>
          Next School
        </span>
      )}
    </span>
  );

  if (!href) return inner;
  return (
    <Link href={href} aria-label="NextSchool — strona główna" className="inline-flex items-center">
      {inner}
    </Link>
  );
}
